import { ConditionParams } from "@multiweb/clients/dataReader";
import { createSetMultiselectModeAction } from "@multiweb/frames";
import { reactControlConnectorLogger } from "@multiweb/reactConnectors/logger";
import { IHasMultiselect, IReactFrameConnector } from "@multiweb/reactConnectors/types";
import { bridge } from "@multiweb/reactCore/bridge";
import {
    createCheckAllRecordsAction,
    createGetCheckedRecordsIds,
    createGetUncheckedRecordsIds,
    getConditionParams,
    getLoadingCondition,
} from "@multiweb/reactCore/datasource";
import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { DataConverter } from "Global/Base/Converters/DataConverter";
import { AnyConstructor, Mixin } from "Global/Base/types";
import { OWObjectList } from "OW.Remoting/Remoting/DataTypes/OWObjectList";
import { ActionContext, ActionType, IAction, IMultiObjectAction, IWFAction } from "UI/UI.Controls/Actions/IAction";
import { XGAction } from "UI/UI/UI/XGControls/Actions/XGAction";
import { FrameSharedMethods } from "UI/UI/UI/XGControls/Frames/FrameSharedMethods";

const { dispatchAction, getState } = bridge;

/**
 * Mixin který přidá podporu pro režim výběru více záznamů.
 *
 * @param Base Base třída rozšiřující IConnectorFrame.
 * @mixin
 */
export const HasMultiselect = <T extends AnyConstructor<IReactFrameConnector>>(Base: T) =>
    class HasMultiselect extends Base implements IHasMultiselect {
        public _multiselectMode: boolean = false;
        public _atLeastOneRowChecked: boolean = false;
        /**
         * Akce, které lze spustit nad více aplikačními záznamy.
         */
        public _multiObjectActions: Array<IMultiObjectAction> = new Array<IMultiObjectAction>();
        public _allowMultiSelect: boolean;
        /**
         * Selector pro získání označených záznamů z redux stavu.
         */
        public readonly _getCheckedRowsIdsSelector = createGetCheckedRecordsIds();
        /**
         * Selector pro získání neoznačených záznamů z redux stavu.
         */
        public readonly _getUncheckedRowsIdsSelector = createGetUncheckedRecordsIds();
        /** @inheritDoc */
        public isSelectedAll: boolean = false;
        /** Akce pro označení více záznamů. */
        public _showMultiselectAction: XGAction | undefined = undefined;
        /** Akce pro zrušení výběru více záznamů. */
        public _hideMultiselectAction: XGAction | undefined = undefined;
        /** Akce pro označení všech záznamů. */
        public _selectAllAction: XGAction | undefined = undefined;

        public get allowMultiSelect(): boolean {
            return this._allowMultiSelect;
        }

        public set allowMultiSelect(value: boolean) {
            this._allowMultiSelect = value;
        }

        public get atLeastOneRowChecked(): boolean {
            return this._atLeastOneRowChecked;
        }

        public set atLeastOneRowChecked(value: boolean) {
            if (this._atLeastOneRowChecked !== value) {
                this._atLeastOneRowChecked = value;
                this._refreshActionsVisibility();
            }
        }

        /** @inheritDoc */
        public get loadingConditionParams(): ConditionParams {
            return getConditionParams(getState(), this.element.id);
        }

        /** @inheritDoc */
        public get selectedIds(): string[] {
            return [...(this._getCheckedRowsIdsSelector(getState(), this.element.id) || EMPTY_ARRAY)];
        }

        /** @inheritDoc */
        public get unselectedIds(): string[] {
            return [...(this._getUncheckedRowsIdsSelector(getState(), this.element.id) || EMPTY_ARRAY)];
        }

        /** @inheritDoc */
        public get loadingCondition(): string {
            return getLoadingCondition(getState(), this.element.id);
        }

        constructor(...args: any[]) {
            super(...args);
            this.allowMultiSelect = DataConverter.toBoolean(this.element.getAttribute("data-allowmultiselect"), true);
        }

        public createSelectionObjectList(): OWObjectList {
            const objList = new OWObjectList();
            objList.className = this.objectName;

            if (this.isSelectedAll) {
                objList.isSelectAll = true;
                objList.condition = this.loadingCondition;
                objList.conditionParams = this.loadingConditionParams;
                this.unselectedIds.forEach(id => objList.add({ id: id, className: this.objectName }));
            } else {
                this.selectedIds.forEach(id => objList.add({ id: id, className: this.objectName }));
            }
            return objList;
        }

        /** @inheritDoc */
        public setMultiselectMode(multiselectMode: boolean, dispatchActions: boolean = true): void {
            if (this._multiselectMode !== multiselectMode) {
                this._multiselectMode = multiselectMode;
                this._refreshActionsVisibility();
                if (dispatchActions) {
                    dispatchAction(createSetMultiselectModeAction(this.element.id, multiselectMode));
                }
            }
        }

        /** @inheritDoc */
        public get multiselectMode(): boolean {
            return this._multiselectMode;
        }

        /**
         * LLF nepodporuje práva, ale potřebuje nastavit hodnoty pro zobrazení MultiObjectAction.
         */
        public _refreshActionsVisibility(): void {
            this._multiObjectActions.filter(i => i.toolbarButton).forEach(action => {
                if (!(action.mainAction instanceof IWFAction)) {
                    action.toolbarButton.visible = this._multiselectMode;
                    action.toolbarButton.enabledVisual = this._multiselectMode && this.atLeastOneRowChecked;
                }
                action.mainAction.visible = !this._multiselectMode;
            });
            this.setRefreshActionVisibility(!this._multiselectMode);
            if (this.allowMultiSelect && this._showMultiselectAction && this._hideMultiselectAction) {
                this._showMultiselectAction.visible = !this._multiselectMode;
                this._hideMultiselectAction.visible = this._multiselectMode;
                if (this._selectAllAction) {
                    this._selectAllAction.visible = this._multiselectMode;
                }
            }
        }

        /**
         * @override {@link IReactConnectorFrame.initializeActions}
         */
        public override async initializeActions(): Promise<void> {
            await super.initializeActions();
            this.addMultiSelectActions();
            this._refreshActionsVisibility();
        }

        /**
         * @override {@link IReactConnectorFrame.setToolbarActionEnabledVisual}
         */
        public override setToolbarActionEnabledVisual(action: IAction, value: boolean): void {
            super.setToolbarActionEnabledVisual(action, value);

            if (action instanceof IMultiObjectAction) {
                if (action.toolbarButton) {
                    // Jestliže jsme v MS módu a není vybrán žádný řádek, tlačítko je disabled a musí tak i zůstat, proto nebudeme nastavovat.
                    if (!(this.multiselectMode && !this.atLeastOneRowChecked)) {
                        action.toolbarButton.enabledVisual = value;
                    }
                } else {
                    action.enabledVisual = value;
                }
            }
        }

        /** @inheritDoc */
        public addMultiSelectActions(): void {
            if (this.allowMultiSelect) {
                this._showMultiselectAction = FrameSharedMethods.createStandardAction(
                    ActionType.standardSelect,
                    async () => {
                        this.setMultiselectMode(true);
                    },
                    this,
                );
                this._showMultiselectAction.visible = this._showMultiselectAction.enabled = true;
                this._showMultiselectAction.enabledVisual = !this.ownerForm.readOnly;
                this.menu.addAction(this._showMultiselectAction);

                if (this.loadOnScroll) {
                    this._selectAllAction = FrameSharedMethods.createStandardAction(
                        ActionType.standardSelectAll,
                        async () => {
                            dispatchAction(createCheckAllRecordsAction(this.element.id, true));
                            this.isSelectedAll = true;
                        },
                        this,
                    );
                    this._selectAllAction.visible = this._selectAllAction.enabled = this._selectAllAction.enabledVisual = true;
                    this.menu.addAction(this._selectAllAction);
                }

                this._hideMultiselectAction = FrameSharedMethods.createStandardAction(
                    ActionType.standardDeselectAll,
                    async () => {
                        dispatchAction(createCheckAllRecordsAction(this.element.id, false));
                        this.setMultiselectMode(false);
                        if (this.isSelectedAll)
                            this.isSelectedAll = false;
                    },
                    this,
                );
                this._hideMultiselectAction.visible = this._hideMultiselectAction.enabled = true;
                this._hideMultiselectAction.enabledVisual = !this.ownerForm.readOnly;
                this.menu.addAction(this._hideMultiselectAction);
            }

            this._multiObjectActions = [...this.menu.getActionsByContext(ActionContext.frameAction)].filter(a => a instanceof IMultiObjectAction) as IMultiObjectAction[];

            if (this._multiObjectActions.some(action => action.mainAction instanceof IWFAction)) {
                reactControlConnectorLogger.warn("WFAction has not implemented execute method for more than one object.");
            }
        }
    };

export type HasMultiselect = Mixin<typeof HasMultiselect>;
